import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, ToggleLeftIcon, ToggleRightIcon, ArrowUpIcon, ArrowDownIcon, FilterIcon, XIcon, CheckIcon } from 'lucide-react';
import { ToleranceRule } from './ToleranceConfiguration';
import { StatusBadge } from '../../StatusBadge';
type SortField = 'toleranceType' | 'value' | 'scope' | 'active';
type SortDirection = 'asc' | 'desc';
interface ToleranceListProps {
  rules: ToleranceRule[];
  onAdd: () => void;
  onEdit: (rule: ToleranceRule) => void;
  onToggleActive: (id: string) => void;
  onDelete: (id: string) => void;
  locale: string;
}
export function ToleranceList({
  rules,
  onAdd,
  onEdit,
  onToggleActive,
  onDelete,
  locale
}: ToleranceListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('toleranceType');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    toleranceType: '',
    scope: '',
    module: '',
    active: ''
  });
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const resetFilters = () => {
    setFilters({
      toleranceType: '',
      scope: '',
      module: '',
      active: ''
    });
    setSearchTerm('');
  };
  // Get all unique modules from rules
  const allModules = Array.from(new Set(rules.flatMap(rule => rule.modules))).filter(module => module !== 'All');
  // Apply filters and sorting
  const filteredRules = rules.filter(rule => {
    // Search term filter (search in scope name)
    const searchMatch = !searchTerm || rule.scopeName && rule.scopeName.toLowerCase().includes(searchTerm.toLowerCase()) || rule.roundingName && rule.roundingName.toLowerCase().includes(searchTerm.toLowerCase());
    // Tolerance type filter
    const typeMatch = !filters.toleranceType || rule.toleranceType === filters.toleranceType;
    // Scope filter
    const scopeMatch = !filters.scope || rule.scope === filters.scope;
    // Module filter
    const moduleMatch = !filters.module || rule.modules.includes(filters.module) || rule.modules.includes('All');
    // Active status filter
    const activeMatch = filters.active === '' || filters.active === 'active' && rule.active || filters.active === 'inactive' && !rule.active;
    return searchMatch && typeMatch && scopeMatch && moduleMatch && activeMatch;
  });
  // Apply sorting
  const sortedRules = [...filteredRules].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'toleranceType':
        comparison = a.toleranceType.localeCompare(b.toleranceType);
        break;
      case 'value':
        comparison = a.value - b.value;
        break;
      case 'scope':
        comparison = a.scope.localeCompare(b.scope);
        break;
      case 'active':
        comparison = Number(a.active) - Number(b.active);
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  return <div className="space-y-6" data-id="element-10142">
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-10143">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-10144">
          <div className="flex-1 max-w-md" data-id="element-10145">
            <div className="relative rounded-md shadow-sm" data-id="element-10146">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10147">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-10148" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search tolerance rules..." data-id="element-10149" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-10150">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10151">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-10152" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-10153">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            <button onClick={onAdd} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10154">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-10155" />
              Add Tolerance Rule
            </button>
          </div>
        </div>
        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-10156">
            {filters.toleranceType && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-10157">
                Type: {filters.toleranceType}
                <button type="button" onClick={() => setFilters({
            ...filters,
            toleranceType: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-10158">
                  <XIcon className="h-3 w-3" data-id="element-10159" />
                </button>
              </span>}
            {filters.scope && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800" data-id="element-10160">
                Scope: {filters.scope}
                <button type="button" onClick={() => setFilters({
            ...filters,
            scope: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none" data-id="element-10161">
                  <XIcon className="h-3 w-3" data-id="element-10162" />
                </button>
              </span>}
            {filters.module && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800" data-id="element-10163">
                Module: {filters.module}
                <button type="button" onClick={() => setFilters({
            ...filters,
            module: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-yellow-400 hover:bg-yellow-200 hover:text-yellow-500 focus:outline-none" data-id="element-10164">
                  <XIcon className="h-3 w-3" data-id="element-10165" />
                </button>
              </span>}
            {filters.active && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-10166">
                Status: {filters.active === 'active' ? 'Active' : 'Inactive'}
                <button type="button" onClick={() => setFilters({
            ...filters,
            active: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-10167">
                  <XIcon className="h-3 w-3" data-id="element-10168" />
                </button>
              </span>}
            <button type="button" onClick={resetFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10169">
              Clear All
            </button>
          </div>}
        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-4" data-id="element-10170">
            <div data-id="element-10171">
              <label htmlFor="toleranceType" className="block text-sm font-medium text-gray-700" data-id="element-10172">
                Tolerance Type
              </label>
              <select id="toleranceType" name="toleranceType" value={filters.toleranceType} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10173">
                <option value="" data-id="element-10174">All Types</option>
                <option value="Amount" data-id="element-10175">Amount</option>
                <option value="Percentage" data-id="element-10176">Percentage</option>
              </select>
            </div>
            <div data-id="element-10177">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-10178">
                Scope
              </label>
              <select id="scope" name="scope" value={filters.scope} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10179">
                <option value="" data-id="element-10180">All Scopes</option>
                <option value="Global" data-id="element-10181">Global</option>
                <option value="Vendor" data-id="element-10182">Vendor</option>
                <option value="Entity" data-id="element-10183">Entity</option>
                <option value="Site" data-id="element-10184">Site</option>
              </select>
            </div>
            <div data-id="element-10185">
              <label htmlFor="module" className="block text-sm font-medium text-gray-700" data-id="element-10186">
                Module
              </label>
              <select id="module" name="module" value={filters.module} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10187">
                <option value="" data-id="element-10188">All Modules</option>
                {['Invoice', 'PO', 'Budget', ...allModules].map(module => <option key={module} value={module} data-id="element-10189">
                    {module}
                  </option>)}
              </select>
            </div>
            <div data-id="element-10190">
              <label htmlFor="active" className="block text-sm font-medium text-gray-700" data-id="element-10191">
                Status
              </label>
              <select id="active" name="active" value={filters.active} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10192">
                <option value="" data-id="element-10193">All Statuses</option>
                <option value="active" data-id="element-10194">Active</option>
                <option value="inactive" data-id="element-10195">Inactive</option>
              </select>
            </div>
          </div>}
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10196">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-10197">
          <thead className="bg-gray-50" data-id="element-10198">
            <tr data-id="element-10199">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('toleranceType')} data-id="element-10200">
                <div className="flex items-center space-x-1" data-id="element-10201">
                  <span data-id="element-10202">Tolerance Type</span>
                  {sortField === 'toleranceType' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-10203" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-10204" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('value')} data-id="element-10205">
                <div className="flex items-center space-x-1" data-id="element-10206">
                  <span data-id="element-10207">Value</span>
                  {sortField === 'value' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-10208" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-10209" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10210">
                Rounding Applied
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('scope')} data-id="element-10211">
                <div className="flex items-center space-x-1" data-id="element-10212">
                  <span data-id="element-10213">Scope</span>
                  {sortField === 'scope' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-10214" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-10215" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10216">
                Module
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('active')} data-id="element-10217">
                <div className="flex items-center space-x-1" data-id="element-10218">
                  <span data-id="element-10219">Status</span>
                  {sortField === 'active' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-10220" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-10221" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10222">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-10223">
            {sortedRules.length === 0 ? <tr data-id="element-10224">
                <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-10225">
                  No tolerance rules found. Click "Add Tolerance Rule" to create
                  one.
                </td>
              </tr> : sortedRules.map(rule => <tr key={rule.id} className="hover:bg-gray-50" data-id="element-10226">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-10227">
                    {rule.toleranceType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-10228">
                    {rule.toleranceType === 'Amount' && rule.value.toLocaleString(locale, {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
                    {rule.toleranceType === 'Percentage' && `${rule.value.toLocaleString(locale)}%`}
                    {rule.toleranceType === 'Combined' && <>
                        <div data-id="element-10229">{rule.percentageValue}%</div>
                        <div className="text-xs text-gray-400" data-id="element-10230">
                          Max:{' '}
                          {rule.maxAmount?.toLocaleString(locale, {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                        </div>
                      </>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-10231">
                    {rule.roundingName || <span className="text-gray-400 italic" data-id="element-10232">None</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-10233">
                    <div data-id="element-10234">
                      {rule.scope}
                      {rule.scopeName && <span className="ml-1 text-xs text-gray-400" data-id="element-10235">
                          ({rule.scopeName})
                        </span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-10236">
                    {rule.modules.includes('All') ? 'All Modules' : rule.modules.join(', ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10237">
                    <StatusBadge status={rule.active ? 'success' : 'error'} text={rule.active ? 'Active' : 'Inactive'} data-id="element-10238" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10239">
                    <div className="flex justify-end space-x-2" data-id="element-10240">
                      <button onClick={() => onEdit(rule)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-10241">
                        <EditIcon className="h-4 w-4" data-id="element-10242" />
                      </button>
                      <button onClick={() => onToggleActive(rule.id)} className={`${rule.active ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'}`} title={rule.active ? 'Deactivate' : 'Activate'} data-id="element-10243">
                        {rule.active ? <ToggleRightIcon className="h-4 w-4" data-id="element-10244" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-10245" />}
                      </button>
                      <button onClick={() => onDelete(rule.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-10246">
                        <TrashIcon className="h-4 w-4" data-id="element-10247" />
                      </button>
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}